import { useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { Loader2, KeyRound, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PasswordInput } from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContractorResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [done, setDone] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) { toast.error("Le mot de passe doit contenir au moins 8 caractères"); return; }
    if (password !== confirm) { toast.error("Les deux mots de passe ne correspondent pas"); return; }
    setIsLoading(true);
    try {
      await axios.post(`${API}/contractors/reset-password`, { token, new_password: password });
      setDone(true);
      toast.success("Mot de passe réinitialisé");
      setTimeout(() => navigate("/espace-entrepreneur/login"), 1800);
    } catch (error) {
      const message = error.response?.data?.detail || "Lien invalide ou expiré";
      toast.error(typeof message === "string" ? message : "Lien invalide ou expiré");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4" data-testid="contractor-reset-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-orange-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <KeyRound className="w-8 h-8 text-white" />
          </div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Nouveau mot de passe</h1>
          <p className="text-slate-600 mt-2">Choisissez un nouveau mot de passe pour votre espace entrepreneur.</p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl shadow-slate-200/50 p-8 border border-slate-100">
          {!token ? (
            <p className="text-sm text-red-600 text-center" data-testid="reset-no-token">Lien invalide : aucun jeton fourni. Refaites une demande depuis « Mot de passe oublié ».</p>
          ) : done ? (
            <div className="text-center" data-testid="reset-success">
              <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-3" />
              <p className="text-slate-700">Mot de passe réinitialisé. Redirection vers la connexion…</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="reset-form">
              <div>
                <Label htmlFor="pwd" className="text-slate-700 mb-2 block">Nouveau mot de passe</Label>
                <PasswordInput id="pwd" placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="reset-password-input" />
              </div>
              <div>
                <Label htmlFor="confirm" className="text-slate-700 mb-2 block">Confirmez le mot de passe</Label>
                <PasswordInput id="confirm" placeholder="••••••••" value={confirm} onChange={(e) => setConfirm(e.target.value)} required data-testid="reset-confirm-input" />
              </div>
              <Button type="submit" disabled={isLoading} className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold py-6" data-testid="reset-submit-button">
                {isLoading ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Réinitialisation…</> : "Réinitialiser le mot de passe"}
              </Button>
            </form>
          )}
        </div>

        <p className="text-center text-sm text-slate-500 mt-6">
          <Link to="/espace-entrepreneur/login" className="hover:text-slate-700">← Retour à la connexion</Link>
        </p>
      </div>
    </div>
  );
}
